import { GridSortModel } from '@mui/x-data-grid';
import React, { useState } from 'react';
import { Accordion, AccordionSummary } from '@biq/apex-ui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TableComponent } from '../../../components/landing-page-wrapper/components/TableComponent';
import {
    ColumnDef,
    getColumns as getDictionaryColumns,
} from '../../dictionaries/dicitionary.helper';
import { getColumns as getEdgeDefinitionColumns } from '../../edge-definitions/edge-definition-helper';
import {
    getUserGroupColumns,
    getSystemWalletColumnsForMetadataSearch,
    getUserWalletColumns,
} from '../../wallets-and-groups/wallets-and-groups-helper';
import {
    ACCORDION_GRID_HEIGHT,
    ACCORDION_GRID_MAX_GRID_SIZE,
    DEFAULT_PAGINATION_SIZE,
    DEFAULT_PAGINATION_SIZE_OPTIONS,
    DrawerItem,
    DrawerItemWithoutValue,
    getGlossaryColumns,
    getDataPipelineColumns,
    getTermsColumns,
    MetadataResultAccordionProps,
    metadataResultStyles,
    metadataSections,
} from './metadata-components.helper';
import { ChainSvg } from '../../../assets';
import { MetadataSearchDrawer } from './MetadataSearchDrawer';

export const ResultsAccordion = ({
    expanded,
    onChange,
    accordionAriaControls,
    accordionSumID,
    accordionHeader,
    data = [],
}: MetadataResultAccordionProps): React.JSX.Element => {
    const [selectedItem, setSelectedItem] = useState<DrawerItem | undefined>(
        undefined
    );

    const gridHeight = Math.min(
        data?.length * 52 + 113,
        window.innerHeight - ACCORDION_GRID_HEIGHT
    );

    const getDrawerInformationForCurrentItem = (
        item: any
    ): DrawerItemWithoutValue => {
        switch (accordionHeader) {
            case metadataSections.dictionaries:
                return { type: 'Dictionary', title: item.name };
            case metadataSections.edgeDefs:
                return { type: 'Edge Definition', title: item.name };
            case metadataSections.systemWallets:
                return { type: 'System Wallet', title: item.description };
            case metadataSections.userGroups:
                return { type: 'User Group', title: item.name };
            case metadataSections.userWallets:
                return { type: 'User Wallet', title: item.fullName };
            case metadataSections.glossaries:
                return { type: 'Glossary', title: item.term };
            case metadataSections.dataPipelines:
                return { type: 'Data Pipeline', title: item.name };
            case metadataSections.terms:
                return { type: 'Term', title: item.name };
            default:
                return { type: 'N/A', title: 'N/A' };
        }
    };

    const handleRowClick = (row: any): void => {
        const value = row.row;
        const drawerItem: DrawerItem = {
            ...getDrawerInformationForCurrentItem(value),
            value,
        };
        setSelectedItem(drawerItem);
    };

    const getColumnsForCurrentKey = (): ColumnDef[] => {
        switch (accordionHeader) {
            case metadataSections.dictionaries:
                return getDictionaryColumns();
            case metadataSections.edgeDefs:
                return getEdgeDefinitionColumns();
            case metadataSections.systemWallets:
                return getSystemWalletColumnsForMetadataSearch();
            case metadataSections.userGroups:
                return getUserGroupColumns();
            case metadataSections.userWallets:
                return getUserWalletColumns(false);
            case metadataSections.glossaries:
                return getGlossaryColumns();
            case metadataSections.dataPipelines:
                return getDataPipelineColumns();
            case metadataSections.terms:
                return getTermsColumns();
            default:
                return [];
        }
    };

    const getDefaultTableSortingByKey = (): GridSortModel | undefined => {
        switch (accordionHeader) {
            case metadataSections.dictionaries:
                return [{ field: 'name', sort: 'asc' }];
            case metadataSections.edgeDefs:
                return [{ field: 'name', sort: 'asc' }];
            case metadataSections.systemWallets:
                return [{ field: 'type', sort: 'asc' }];
            case metadataSections.userGroups:
                return [{ field: 'name', sort: 'asc' }];
            case metadataSections.userWallets:
                return [{ field: 'fullName', sort: 'asc' }];
            case metadataSections.glossaries:
                return [{ field: 'term', sort: 'asc' }];
            case metadataSections.dataPipelines:
                return [{ field: 'name', sort: 'asc' }];
            case metadataSections.terms:
                return [{ field: 'name', sort: 'asc' }];
            default:
                return undefined;
        }
    };

    const handleDrawerClose = (): void => {
        setSelectedItem(undefined);
    };

    return (
        <Accordion expanded={expanded} onChange={onChange} variant='outlined'>
            <AccordionSummary
                aria-controls={accordionAriaControls}
                id={accordionSumID}
                expandIcon={<ExpandMoreIcon />}
                header={accordionHeader}
                badgeCount={data.length}
            />
            <TableComponent
                customDataGridStyles={metadataResultStyles.tableComponent}
                customEmptyStateContainerStyles={
                    metadataResultStyles.accordionEmptyStateContainer
                }
                customImageStyles={metadataResultStyles.emptyStateImage}
                customHeadingContainerStyles={
                    metadataResultStyles.emptyStateHeadingContainer
                }
                customHeadingTextStyles={
                    metadataResultStyles.emptyStateHeadingTextContainer
                }
                isAccordionContainer
                variantType='borderless'
                tableData={data}
                gridHeight={gridHeight}
                maxGridHeight={ACCORDION_GRID_MAX_GRID_SIZE}
                tableHeight={ACCORDION_GRID_HEIGHT}
                autoRowHeight={accordionHeader !== metadataSections.userWallets}
                isGridHeightAuto
                columns={getColumnsForCurrentKey()}
                getRowId={(row: any): string => Object.values(row).join('_')}
                onRowClick={(row): void => {
                    handleRowClick(row);
                }}
                paginationpageSizeOptions={DEFAULT_PAGINATION_SIZE_OPTIONS}
                paginationPageSize={DEFAULT_PAGINATION_SIZE}
                emptyStateIcon={ChainSvg}
                emptyStateBodyMessage={''}
                readonly={false}
                readonlyTooltipTitle={''}
                sortModel={getDefaultTableSortingByKey()}
            />
            {selectedItem && (
                <MetadataSearchDrawer
                    onDrawerClose={handleDrawerClose}
                    item={selectedItem}
                />
            )}
        </Accordion>
    );
};
